import React, { useMemo, useEffect } from 'react';

import { useInView } from 'react-intersection-observer';
import { useDispatch, useSelector } from 'react-redux';

import PageHeader from '@atlaskit/page-header';
import { usePageLayoutResize } from '@atlaskit/page-layout';
import { Text } from '@atlaskit/primitives';

import { useConsentBanner } from 'src/app/navigation-provider';
import { relativeDateString } from 'src/components/date';
import RenderedTitle from 'src/components/rendered-title';
import StickyHeader from 'src/components/sticky-header/src/components/sticky-header';
import { UserAvatar } from 'src/containers/user-avatar';
import { useIntl } from 'src/hooks/intl';
import { TOGGLE_STICKY_HEADER_ACTIVE_STATUS } from 'src/redux/pull-request/actions/constants';
import { getCurrentPullRequest } from 'src/redux/pull-request/selectors';
import { PageHeaderColumn } from 'src/sections/global/components/page.style';
import RepositoryBreadcrumbs from 'src/sections/repository/containers/breadcrumbs';
import {
  getCombinedBannerAndHorizontalNavHeight,
  getIsMobileHeaderActive,
} from 'src/selectors/global-selectors';
import { BucketState } from 'src/types/state';

import PullRequestBreadcrumbs from '../../../containers/breadcrumbs';
import BranchesAndState from '../../branches-and-state';
import { EditableTitle } from '../../editable-title';
import { HeaderActions } from '../../header-actions';
import messages from '../../header.i18n';
import {
  ActionsWrapper,
  Avatar,
  PullRequestAuthor,
  PullRequestInfo,
} from '../../header.style';
import { calculateOffsetForNavNext } from '../../utils/calculate-header-offset';

import { CompactHeader } from './compact-header';

type HeaderProps = {
  cssVarContainerRef: React.RefObject<HTMLDivElement | null>;
  isLoadingConflicts: boolean;
};

// This is similar to src/sections/repository/sections/pull-request/components/header.tsx
// but with some modifications for the new tabbed PR view.
export const Header = ({
  cssVarContainerRef,
  isLoadingConflicts,
}: HeaderProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { consentBannerHeight } = useConsentBanner()[0];
  const currentPullRequest = useSelector(getCurrentPullRequest);
  const isMobileHeaderActive = useSelector(getIsMobileHeaderActive);
  const stickyHeaderTopOffset = useSelector((state: BucketState) =>
    getCombinedBannerAndHorizontalNavHeight(state, false, consentBannerHeight)
  );
  const { isLeftSidebarCollapsed, leftSidebarState } = usePageLayoutResize();

  const [ref, isStaticHeaderVisible] = useInView({
    // Note: DO NOT set a threshold other than default value because
    // it will cause the header/compact header to jitter when
    // scrolling down in some edge cases.
    threshold: 0, // 0 is default value
    rootMargin: `-${stickyHeaderTopOffset}px 0px 0px 0px`,
    initialInView: true,
  });

  useEffect(() => {
    dispatch({
      type: TOGGLE_STICKY_HEADER_ACTIVE_STATUS,
      payload: !isStaticHeaderVisible,
    });
  }, [isStaticHeaderVisible, dispatch]);

  const authorStatus = useMemo(() => {
    if (!currentPullRequest) {
      return null;
    }

    const { participants = [], author } = currentPullRequest;
    const hasAuthorApproved = participants.some(
      p => !!p.user && !!author && p.user.uuid === author.uuid && p.approved
    );
    if (hasAuthorApproved) {
      return 'approved';
    }

    return undefined;
  }, [currentPullRequest]);

  if (!currentPullRequest) {
    return null;
  }

  const {
    id,
    author,
    state,
    draft = false,
    rendered,
    created_on: createdOn,
    closed_on: closedOn,
    updated_on: updatedOn,
  } = currentPullRequest;

  return (
    <>
      <PageHeaderColumn data-testid="pr-header" tabbedLayout ref={ref}>
        <PageHeader
          breadcrumbs={
            <RepositoryBreadcrumbs>
              <PullRequestBreadcrumbs />
            </RepositoryBreadcrumbs>
          }
          bottomBar={
            <ActionsWrapper isStaticHeaderVisible={isStaticHeaderVisible}>
              <PullRequestAuthor>
                <Avatar data-qa="pr-header-author">
                  <UserAvatar
                    profileCardPosition="bottom-start"
                    user={author}
                    status={authorStatus}
                  />
                </Avatar>
                <PullRequestInfo>
                  <BranchesAndState
                    pullRequestState={state}
                    pullRequestDraft={draft}
                    pullRequestCreatedOn={createdOn}
                    pullRequestClosedOn={closedOn}
                  />
                  <Text size="small" color="color.text.subtlest">
                    {intl.formatMessage(messages.pullRequestSummary, {
                      id: `${id} •`,
                      created: `${relativeDateString(createdOn, intl)} •`,
                      updated: relativeDateString(updatedOn, intl),
                    })}
                  </Text>
                </PullRequestInfo>
              </PullRequestAuthor>
              <HeaderActions includeDiffActions={false} />
            </ActionsWrapper>
          }
        >
          {state === 'OPEN' ? (
            <EditableTitle renderedTitle={rendered.title} />
          ) : (
            <RenderedTitle renderedContent={rendered.title} />
          )}
        </PageHeader>
      </PageHeaderColumn>
      <StickyHeader
        offset={calculateOffsetForNavNext(
          !isLeftSidebarCollapsed,
          isMobileHeaderActive,
          0,
          leftSidebarState.leftSidebarWidth
        )}
        topOffset={stickyHeaderTopOffset}
      >
        {!isStaticHeaderVisible && (
          <CompactHeader
            title={rendered.title.raw}
            state={state}
            draft={draft}
            createdOn={createdOn}
            closedOn={closedOn}
            isLoadingConflicts={isLoadingConflicts}
            cssVarContainerRef={cssVarContainerRef}
          />
        )}
      </StickyHeader>
    </>
  );
};
